import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import {
  CustomDonutChart,
  Typography,
  colors,
  IconGraphy,
  Avatar,
  styles,
  Tooltip,
} from '@leapeasy/ui-kit';
import { formatDashboardCount } from 'utils/formatDashboardCount';

function createData(stageNumber, stage, app, percent, background, color) {
  return {
    stageNumber,
    stage,
    app,
    percent,
    background,
    color,
  };
}

export const CancellationsByCancellationReasonChart = ({ data, onDrilldown }) => {
  const {
    accidentalCancelCount,
    feeCancelCount,
    cosignerCancelCount,
    verifyCancelCount,
    noresponseCancelCount,
    depositCancelCount,
    testCancelCount,
    otherCancelCount,
    cancelledCount,
  } = data;
  const safeCancelDenom = cancelledCount > 0 ? cancelledCount : 1;

  const cancelReasonColors = [
    colors.parisGreen[100],
    colors.purpleIrish[100],
    colors.warmBlue[100],
    colors.blue[600],
    colors.sunglow[100],
    colors.tomato[100],
    colors.magentaRed[100],
    colors.purple[100],
  ];

  const cancellationReasonList = [
    createData(
      1,
      'Accidental Application',
      accidentalCancelCount,
      accidentalCancelCount / safeCancelDenom,
      colors.parisGreen[50],
      colors.parisGreen[200],
    ),
    createData(
      2,
      'Cannot afford fee',
      feeCancelCount,
      feeCancelCount / safeCancelDenom,
      colors.purpleIrish[50],
      colors.purpleIrish[200],
    ),
    createData(
      3,
      'Found own cosigner',
      cosignerCancelCount,
      cosignerCancelCount / safeCancelDenom,
      colors.warmBlue[50],
      colors.warmBlue[200],
    ),
    createData(
      4,
      'Cannot Verify Documentation',
      verifyCancelCount,
      verifyCancelCount / safeCancelDenom,
      colors.blue[200],
      colors.blue[600],
    ),
    createData(
      5,
      'No Response',
      noresponseCancelCount,
      noresponseCancelCount / safeCancelDenom,
      colors.sunglow[50],
      colors.sunglow[200],
    ),
    createData(
      6,
      'Paid additional deposit',
      depositCancelCount,
      depositCancelCount / safeCancelDenom,
      colors.tomato[50],
      colors.tomato[200],
    ),
    createData(
      7,
      'Test Application',
      testCancelCount,
      testCancelCount / safeCancelDenom,
      colors.magentaRed[50],
      colors.magentaRed[200],
    ),
    createData(
      8,
      'Other',
      otherCancelCount,
      otherCancelCount / safeCancelDenom,
      colors.purple[100],
      colors.purple[600],
    ),
    createData(9, 'Total', cancelledCount, 1, '#F3F1F4', 'black'),
  ];

  return (
    <Box
      sx={{
        boxShadow: styles.boxShadow.sm,
        borderRadius: `${styles.borderRadius.large}px`,
        border: `1px solid ${colors.black[300]}`,
        width: '100%',
        padding: '16px',
        boxSizing: 'border-box',
      }}
      display="flex"
      flexDirection="column"
      gap="12px"
    >
      <Box display="flex" gap="16px">
        <Avatar size="large" iconImage={<IconGraphy icon="FileFolder.ContentCancel" />} />
        <Box>
          <Typography variant="h4" style={{ color: colors.purple[900], fontWeight: '400' }}>
            Cancellations by Cancellation Reason
          </Typography>

          <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '400' }}>
            Breakdown of application cancellations by reason, showing the total number and
            percentage for each category.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          background: 'rgba(249, 248, 249, 0.8)',
          border: `1px solid ${colors.purple[100]}`,
          borderRadius: '8px',
          padding: '16px',
          width: '100%',
          boxSizing: 'border-box',
        }}
        display="flex"
        gap="20px"
      >
        <Box
          sx={{
            flex: '1 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: '4px',
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
          display="flex"
          flexDirection="column"
          gap="24px"
        >
          <Table
            sx={{
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                borderBottom: 'none',
                padding: '4px 12px',
                verticalAlign: 'middle',
              },
              '& .MuiTableBody-root .MuiTableCell-root': {
                height: '20px',
                fontSize: '10px',
                lineHeight: '15px',
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                height: '32px',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '19.8px',
                color: colors.purple[900],
              },
            }}
          >
            <TableHead sx={{ background: '#F9F8F9' }}>
              <TableRow>
                <TableCell sx={{ width: '24px' }}></TableCell>
                <TableCell variant="head" className="p-2">
                  Stage
                </TableCell>
                <TableCell variant="head" className="p-2">
                  #Apps
                </TableCell>
                <TableCell variant="head" className="p-2">
                  Total %
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cancellationReasonList.map((n, index) => {
                const rowClickable = !!onDrilldown && n.stage !== 'Total';
                return (
                  <TableRow
                    key={`${n.stage}-${index}`}
                    hover={rowClickable}
                    onClick={() => rowClickable && onDrilldown()}
                    sx={
                      rowClickable
                        ? {
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(112, 37, 114, 0.06)' },
                          }
                        : undefined
                    }
                  >
                    <TableCell>
                      <Box
                        sx={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          background: n.background,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="small" style={{ fontWeight: '700', color: n.color }}>
                          {n.stageNumber}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={n.stage} placement="top" showCarot>
                        <Box sx={{ width: 'fit-content', maxWidth: '100%' }}>
                          <Typography variant="small" style={{ fontWeight: '500' }}>
                            {n.stage}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="small" style={{ fontWeight: '500' }}>
                        {formatDashboardCount(n.app)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="small" style={{ fontWeight: '500' }}>
                        {n.stage !== 'Total' ? (n.percent * 100)?.toFixed(2) : 100}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            flex: '1 1 0%',
            background: 'white',
            border: `1px solid ${colors.black[200]}`,
            borderRadius: '4px',
            padding: '20px',
            boxSizing: 'border-box',
          }}
          display="flex"
          justifyContent="center"
          flexDirection="column"
          gap="20px"
        >
          <CustomDonutChart
            chartWidth={200}
            chartHeight={200}
            donutMinAngle={30}
            labelItemBackgroundColor="#F9F8F9"
            data={cancellationReasonList
              .filter((reason) => reason.stage !== 'Total')
              .map((reason) => ({
                name: reason.stage,
                value: (reason.app / safeCancelDenom) * 100,
              }))}
            isPercentageValue={true}
            colors={cancelReasonColors}
            title="Total Cancellation"
            description={formatDashboardCount(cancelledCount)}
          />
        </Box>
      </Box>
    </Box>
  );
};
